import { Position } from '@turf/turf';
import {
  FieldBoundarySuggestionOptions,
  LatLngPoint,
} from 'app/utils/field-boundary/types';

export const METERS_PER_DEGREE_LATITUDE = 111320;
const DEGREES_TO_RADIANS = Math.PI / 180;

/**
 * Edge sampling step, in cells. Below one cell so that thin trail polygons
 * still leave a continuous footprint on the raster.
 */
const EDGE_SAMPLING_STEP_CELLS = 0.5;

/** Offset from a cell corner to its center, in cells. */
const CELL_CENTER_OFFSET = 0.5;

/** Growth applied to the cell size while the raster exceeds `maxGridCells`. */
const CELL_SIZE_GROWTH_FACTOR = 1.25;

/** Floor for `maxGridCells`, so the cell size growth loop always ends. */
const MIN_GRID_CELLS = 100;

/**
 * Empty cells kept beyond the dilation reach: the grid border must stay empty
 * for the hole filling and the erosions.
 */
const BORDER_MARGIN_CELLS = 1;

export const CELL_EMPTY = 0;
export const CELL_FILLED = 1;
export const NO_INDEX = -1;

export interface CoverageGrid {
  cells: Uint8Array;
  columns: number;
  rows: number;
  cellSizeMeters: number;
  paddingCells: number;
  closingRadiusCells: number;
  originLat: number;
  originLng: number;
  metersPerDegreeLng: number;
}

interface GridLayout {
  columns: number;
  rows: number;
  paddingCells: number;
  closingRadiusCells: number;
}

interface Bounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

interface ProjectedBounds extends Bounds {
  metersPerDegreeLng: number;
  widthMeters: number;
  heightMeters: number;
}

/** Meters per degree of longitude at `lat` (equirectangular projection). */
export function metersPerDegreeLngAt(lat: number): number {
  return METERS_PER_DEGREE_LATITUDE * Math.cos(lat * DEGREES_TO_RADIANS);
}

function computeGridLayout(
  widthMeters: number,
  heightMeters: number,
  cellSizeMeters: number,
  settings: FieldBoundarySuggestionOptions,
): GridLayout {
  const closingRadiusCells = Math.max(
    0,
    Math.ceil(settings.gapClosingMeters / cellSizeMeters),
  );
  const paddingCells = closingRadiusCells + BORDER_MARGIN_CELLS;
  return {
    columns: Math.floor(widthMeters / cellSizeMeters) + 1 + 2 * paddingCells,
    rows: Math.floor(heightMeters / cellSizeMeters) + 1 + 2 * paddingCells,
    paddingCells,
    closingRadiusCells,
  };
}

/**
 * Bounds of the polygons on a local equirectangular projection: accurate
 * enough over a field and reversible with the same factors, so no distortion
 * leaks into the output.
 */
function projectedBounds(polygons: LatLngPoint[][]): ProjectedBounds {
  const bounds: Bounds = {
    minLat: Infinity,
    maxLat: -Infinity,
    minLng: Infinity,
    maxLng: -Infinity,
  };
  polygons.forEach((path) =>
    path.forEach((point) => {
      bounds.minLat = Math.min(bounds.minLat, point.lat);
      bounds.maxLat = Math.max(bounds.maxLat, point.lat);
      bounds.minLng = Math.min(bounds.minLng, point.lng);
      bounds.maxLng = Math.max(bounds.maxLng, point.lng);
    }),
  );
  const metersPerDegreeLng = metersPerDegreeLngAt(
    (bounds.minLat + bounds.maxLat) / 2,
  );
  return {
    ...bounds,
    metersPerDegreeLng,
    widthMeters: (bounds.maxLng - bounds.minLng) * metersPerDegreeLng,
    heightMeters: (bounds.maxLat - bounds.minLat) * METERS_PER_DEGREE_LATITUDE,
  };
}

function gridCellLimit(settings: FieldBoundarySuggestionOptions): number {
  return Math.max(settings.maxGridCells, MIN_GRID_CELLS);
}

/** Whether the polygons fit the grid at `cellSizeMeters`, without coarsening it. */
export function fitsAtBaseResolution(
  polygons: LatLngPoint[][],
  settings: FieldBoundarySuggestionOptions,
): boolean {
  const { widthMeters, heightMeters } = projectedBounds(polygons);
  const layout = computeGridLayout(
    widthMeters,
    heightMeters,
    settings.cellSizeMeters,
    settings,
  );
  return layout.columns * layout.rows <= gridCellLimit(settings);
}

/** Creates an empty raster over the polygons, coarsened to fit `maxGridCells`. */
export function createGrid(
  polygons: LatLngPoint[][],
  settings: FieldBoundarySuggestionOptions,
): CoverageGrid {
  const { minLat, minLng, metersPerDegreeLng, widthMeters, heightMeters } =
    projectedBounds(polygons);
  const maxGridCells = gridCellLimit(settings);

  let cellSizeMeters = settings.cellSizeMeters;
  let layout = computeGridLayout(
    widthMeters,
    heightMeters,
    cellSizeMeters,
    settings,
  );
  while (layout.columns * layout.rows > maxGridCells) {
    cellSizeMeters *= CELL_SIZE_GROWTH_FACTOR;
    layout = computeGridLayout(
      widthMeters,
      heightMeters,
      cellSizeMeters,
      settings,
    );
  }

  return {
    cells: new Uint8Array(layout.columns * layout.rows),
    columns: layout.columns,
    rows: layout.rows,
    cellSizeMeters,
    paddingCells: layout.paddingCells,
    closingRadiusCells: layout.closingRadiusCells,
    originLat: minLat,
    originLng: minLng,
    metersPerDegreeLng,
  };
}

function toGridX(grid: CoverageGrid, lng: number): number {
  return (
    ((lng - grid.originLng) * grid.metersPerDegreeLng) / grid.cellSizeMeters +
    grid.paddingCells
  );
}

function toGridY(grid: CoverageGrid, lat: number): number {
  return (
    ((lat - grid.originLat) * METERS_PER_DEGREE_LATITUDE) /
      grid.cellSizeMeters +
    grid.paddingCells
  );
}

/** Converts a position in grid units (cells) back to [lng, lat]. */
export function gridToPosition(grid: CoverageGrid, [x, y]: Position): Position {
  return [
    grid.originLng +
      ((x - grid.paddingCells) * grid.cellSizeMeters) / grid.metersPerDegreeLng,
    grid.originLat +
      ((y - grid.paddingCells) * grid.cellSizeMeters) /
        METERS_PER_DEGREE_LATITUDE,
  ];
}

export function isFilledCell(
  grid: CoverageGrid,
  column: number,
  row: number,
): boolean {
  return (
    column >= 0 &&
    column < grid.columns &&
    row >= 0 &&
    row < grid.rows &&
    grid.cells[row * grid.columns + column] === CELL_FILLED
  );
}

function markCell(grid: CoverageGrid, column: number, row: number): void {
  if (column >= 0 && column < grid.columns && row >= 0 && row < grid.rows) {
    grid.cells[row * grid.columns + column] = CELL_FILLED;
  }
}

function markEdges(grid: CoverageGrid, xs: number[], ys: number[]): void {
  for (let i = 0; i < xs.length; i++) {
    const next = (i + 1) % xs.length;
    const deltaX = xs[next] - xs[i];
    const deltaY = ys[next] - ys[i];
    const steps = Math.max(
      1,
      Math.ceil(Math.hypot(deltaX, deltaY) / EDGE_SAMPLING_STEP_CELLS),
    );
    for (let step = 0; step <= steps; step++) {
      const ratio = step / steps;
      markCell(
        grid,
        Math.floor(xs[i] + ratio * deltaX),
        Math.floor(ys[i] + ratio * deltaY),
      );
    }
  }
}

/** First and last cell index whose center lies in [start, end). */
function centersWithin(start: number, end: number): [number, number] {
  return [
    Math.ceil(start - CELL_CENTER_OFFSET),
    Math.ceil(end - CELL_CENTER_OFFSET) - 1,
  ];
}

/**
 * Fills the cells whose center is inside the polygon (even-odd rule), with a
 * scanline: every edge records where it crosses the center line of the rows
 * it spans, and the cells between each pair of crossings are filled. Linear in
 * vertices, crossings and filled cells, however long or slanted the polygon.
 */
export function fillGridPolygonInterior(
  grid: CoverageGrid,
  xs: number[],
  ys: number[],
): void {
  let minY = Infinity;
  let maxY = -Infinity;
  ys.forEach((y) => {
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  });
  const [spanFirstRow, spanLastRow] = centersWithin(minY, maxY);
  const firstRow = Math.max(0, spanFirstRow);
  const lastRow = Math.min(grid.rows - 1, spanLastRow);
  if (lastRow < firstRow) {
    return;
  }

  const crossings: number[][] = Array.from(
    { length: lastRow - firstRow + 1 },
    () => [],
  );
  let j = xs.length - 1;
  for (let i = 0; i < xs.length; i++) {
    const [edgeFirstRow, edgeLastRow] = centersWithin(
      Math.min(ys[i], ys[j]),
      Math.max(ys[i], ys[j]),
    );
    for (
      let row = Math.max(firstRow, edgeFirstRow);
      row <= Math.min(lastRow, edgeLastRow);
      row++
    ) {
      const y = row + CELL_CENTER_OFFSET;
      crossings[row - firstRow].push(
        xs[i] + ((y - ys[i]) * (xs[j] - xs[i])) / (ys[j] - ys[i]),
      );
    }
    j = i;
  }

  crossings.forEach((rowCrossings, offset) => {
    const rowStart = (firstRow + offset) * grid.columns;
    rowCrossings.sort((a, b) => a - b);
    for (let pair = 0; pair + 1 < rowCrossings.length; pair += 2) {
      const [firstColumn, lastColumn] = centersWithin(
        rowCrossings[pair],
        rowCrossings[pair + 1],
      );
      for (
        let column = Math.max(0, firstColumn);
        column <= Math.min(grid.columns - 1, lastColumn);
        column++
      ) {
        grid.cells[rowStart + column] = CELL_FILLED;
      }
    }
  });
}

/**
 * Conservative rasterization: marks every cell crossed by an edge plus every
 * cell whose center is inside the polygon.
 */
export function rasterizePolygon(
  grid: CoverageGrid,
  path: LatLngPoint[],
): void {
  const xs = path.map((point) => toGridX(grid, point.lng));
  const ys = path.map((point) => toGridY(grid, point.lat));
  markEdges(grid, xs, ys);
  fillGridPolygonInterior(grid, xs, ys);
}

/** New raster of the grid's size where the cells matching `predicate` are filled. */
export function cellsWhere(
  grid: CoverageGrid,
  predicate: (index: number) => boolean,
): Uint8Array {
  const cells = new Uint8Array(grid.cells.length);
  for (let index = 0; index < cells.length; index++) {
    if (predicate(index)) {
      cells[index] = CELL_FILLED;
    }
  }
  return cells;
}
